import React, { useEffect, useRef, useState } from "react";
import { TOKEN_KEY, BACKEND_URL } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Activity, Wifi } from "lucide-react";

const SECTOR_LABEL = (s) => s.replace(/-/g, " ");

function wsBaseUrl() {
  const http = BACKEND_URL || "";
  return http.replace(/^http/, "ws");
}

export default function LiveLeadFeed() {
  const [leads, setLeads] = useState([]);
  const [status, setStatus] = useState("connecting"); // connecting | live | offline
  const wsRef = useRef(null);
  const reconnectRef = useRef(null);

  useEffect(() => {
    let mounted = true;
    const connect = () => {
      const token = localStorage.getItem(TOKEN_KEY);
      if (!token) return;
      try {
        const ws = new WebSocket(`${wsBaseUrl()}/api/ws/leads?token=${encodeURIComponent(token)}`);
        wsRef.current = ws;
        ws.onopen = () => { if (mounted) setStatus("live"); };
        ws.onclose = () => {
          if (!mounted) return;
          setStatus("offline");
          // retry in 4s
          reconnectRef.current = setTimeout(connect, 4000);
        };
        ws.onerror = () => { try { ws.close(); } catch {} };
        ws.onmessage = (e) => {
          try {
            const msg = JSON.parse(e.data);
            if (msg.type === "lead.new" && msg.lead) {
              setLeads((prev) => [{ ...msg.lead, _new: true }, ...prev].slice(0, 12));
              // remove _new flag after animation
              setTimeout(() => {
                setLeads((prev) => prev.map((l) => l.id === msg.lead.id ? { ...l, _new: false } : l));
              }, 1400);
            }
          } catch {}
        };
      } catch {
        setStatus("offline");
      }
    };
    connect();
    return () => {
      mounted = false;
      clearTimeout(reconnectRef.current);
      try { wsRef.current?.close(); } catch {}
    };
  }, []);

  return (
    <Card className="rounded-xl shadow-soft border-slate-200 overflow-hidden" data-testid="live-lead-feed">
      <CardContent className="p-0">
        <div className="px-5 py-3.5 border-b border-slate-100 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <span className="inline-grid place-items-center size-8 rounded-md bg-primary/10 text-primary"><Activity className="size-4" /></span>
            <div className="leading-tight">
              <div className="text-sm font-medium text-slate-900">Live lead feed</div>
              <div className="text-[11px] text-slate-500">WebSocket · pushed in real time</div>
            </div>
          </div>
          <StatusBadge status={status} />
        </div>
        <ul className="divide-y divide-slate-100 max-h-[420px] overflow-y-auto">
          {leads.length === 0 && (
            <li className="px-5 py-8 text-center text-sm text-slate-500">Listening for new leads… the next one usually lands within 14 seconds.</li>
          )}
          {leads.map((l) => (
            <li key={l.id} className={`px-5 py-3 flex items-center gap-3 transition-colors ${l._new ? "bg-primary/[0.04]" : ""}`}>
              <span className={`size-2 rounded-full ${l._new ? "bg-emerald-500 animate-ping-slow" : "bg-emerald-500/60"}`} />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-slate-900 truncate">{l.first_name} {l.last_name} <span className="text-slate-400 text-xs font-normal">· {l.city}</span></div>
                <div className="text-[11px] text-slate-500 capitalize truncate">{SECTOR_LABEL(l.sector)} · {l.acquisition_type.replace("-", " ")} · {l.source}</div>
              </div>
              <div className="text-right shrink-0">
                <div className="font-mono tabular-nums text-sm text-slate-900">£{l.price.toFixed(2)}</div>
                <div className="text-[10px] uppercase tracking-wider text-slate-500">{l.status}</div>
              </div>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}

function StatusBadge({ status }) {
  const cfg = {
    live: { c: "bg-emerald-50 text-emerald-700 border-emerald-200", d: "bg-emerald-500", l: "Live" },
    connecting: { c: "bg-amber-50 text-amber-700 border-amber-200", d: "bg-amber-500", l: "Connecting" },
    offline: { c: "bg-slate-100 text-slate-600 border-slate-200", d: "bg-slate-400", l: "Reconnecting" },
  }[status];
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider ${cfg.c}`}>
      <span className={`size-1.5 rounded-full ${cfg.d} ${status === "live" ? "animate-pulse" : ""}`} />
      <Wifi className="size-3" /> {cfg.l}
    </span>
  );
}
